/**
 * Reusable Accordion Web Component
 *
 * Usage:
 * <rtnu-accordion>
 *   <div class="accordion" data-single-open> <!-- data-single-open is optional -->
 *     <section>
 *       <h3>
 *         <button id="item-1-button" aria-expanded="false" aria-controls="item-1" data-is-open="false">
 *           <span>Title</span>
 *           <svg class="icon-plus">...</svg>
 *           <svg class="icon-minus">...</svg>
 *         </button>
 *       </h3>
 *       <div id="item-1" aria-labelledby="item-1-button" class="accordion-content">
 *         <div>Content here</div>
 *       </div>
 *     </section>
 *   </div>
 * </rtnu-accordion>
 */

class RtnuAccordion extends HTMLElement {
  constructor() {
    super();
    this.buttons = [];
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find all accordion buttons within this component
    this.buttons = Array.from(this.querySelectorAll('.accordion button'));

    if (this.buttons.length === 0) return;

    // Attach click events
    this.buttons.forEach(button => {
      button.addEventListener('click', () => this.toggle(button));
    });

    // Set initial heights for proper animation
    this.buttons.forEach(button => {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (content) {
        content.style.transition = 'height 0.3s ease, opacity 0.3s ease';
        if (button.getAttribute('aria-expanded') === 'false') {
          content.style.height = '0px';
          content.style.opacity = '0';
        }
      }
    });
  }

  toggle(button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    const content = document.getElementById(button.getAttribute('aria-controls'));
    const inner = content?.querySelector('div');

    if (!content || !inner) return;

    // Update ARIA attributes
    button.setAttribute('aria-expanded', !expanded);
    button.setAttribute('data-is-open', !expanded);

    if (!expanded) {
      // Open accordion
      content.style.height = `${inner.offsetHeight}px`;
      content.style.opacity = '1';

      // Close other accordions if single-open mode
      const accordion = button.closest('.accordion');
      if (accordion?.hasAttribute('data-single-open')) {
        this.buttons.forEach(otherButton => {
          if (otherButton !== button && otherButton.getAttribute('aria-expanded') === 'true') {
            this.toggle(otherButton);
          }
        });
      }
    } else {
      // Close accordion
      content.style.height = '0px';
      content.style.opacity = '0';
    }
  }
}

if (!customElements.get('rtnu-accordion')) {
  customElements.define('rtnu-accordion', RtnuAccordion);
}

export default RtnuAccordion;
